import { ColumnConfig } from '../Shared/Components/DataTable';
import { Reservation } from './Types/reservationTypes';
import { Box, Chip, Typography } from '@mui/material';
import { PaymentMethodTypeLabels } from '../Invoices/Types/invoicesLabels';
import dayjs from 'dayjs';
import type { ReservationPartyLookup } from './Service/reservationPartiesLookupMapper';
import { getReservationApplicantDisplay } from './Helpers/getReservationApplicantDisplay';
import ReservationInvoiceLinksCell from './Components/ReservationInvoiceLinksCell/ReservationInvoiceLinksCell';
import { formatPaymentStatusLabel, getPaymentStatusColor, getPaymentStatusAmount, normalizePaymentStatus } from './Helpers/paymentStatusHelpers';

interface ReservationColumnsOptions {
  applicantDisplayMode?: 'split' | 'summary';
  parties?: ReservationPartyLookup[];
}

const SUMMARY_WIDTHS = {
  createdAt: '7%',
  unitPrice: '7%',
  offerName: '19%',
  applicantDisplay: '14%',
  effectiveParticipantsCount: '5%',
  totalPrice: '6%',
  paymentStatus: '8%',
  totalPaidAmount: '7%',
  invoices: '15%',
  status: '12%',
} as const;

const renderReservationStatus = (row: Reservation) => (
  <Chip
    label={row.status === 'cancelled' ? 'Anulowana' : 'Aktywna'}
    color={row.status === 'cancelled' ? 'default' : 'success'}
    size="small"
  />
);

const getSummaryColumnWidth = (
  applicantDisplayMode: ReservationColumnsOptions['applicantDisplayMode'],
  key: string
): string | undefined => {
  if (applicantDisplayMode !== 'summary') {
    return undefined;
  }

  return SUMMARY_WIDTHS[key as keyof typeof SUMMARY_WIDTHS];
};

const renderOfferName = (row: Reservation, applicantDisplayMode: 'split' | 'summary') => {
  const offerName = row.offerName || row.booking?.offerName;

  if (applicantDisplayMode !== 'summary') {
    return offerName || '-';
  }

  return (
    <Typography
      variant="body2"
      sx={{
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        lineHeight: 1.35,
      }}
    >
      {offerName || '-'}
    </Typography>
  );
};

const renderApplicantDisplay = (row: Reservation, parties: ReservationPartyLookup[]) => {
  const applicant = getReservationApplicantDisplay(row, parties);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          lineHeight: 1.35,
          wordBreak: 'break-word',
        }}
      >
        {applicant.title}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          lineHeight: 1.35,
          wordBreak: 'break-word',
        }}
      >
        {applicant.subtitle || '-'}
      </Typography>
    </Box>
  );
};

const createReservationSummaryColumns = (
  parties: ReservationPartyLookup[]
): ColumnConfig<Reservation>[] => [
  {
    key: 'createdAt',
    label: 'Data',
    width: getSummaryColumnWidth('summary', 'createdAt'),
    render: (row) => dayjs(row.createdAt).format('DD.MM.YYYY'),
  },
  {
    key: 'unitPrice',
    label: 'Cena jedn.',
    width: getSummaryColumnWidth('summary', 'unitPrice'),
    render: (row) => `${row.unitPrice} PLN`,
  },
  {
    key: 'offerName',
    label: 'Nazwa oferty',
    width: getSummaryColumnWidth('summary', 'offerName'),
    render: (row) => renderOfferName(row, 'summary'),
  },
  {
    key: 'applicantDisplay',
    label: 'Zgłaszający',
    width: getSummaryColumnWidth('summary', 'applicantDisplay'),
    render: (row) => renderApplicantDisplay(row, parties),
  },
  {
    key: 'effectiveParticipantsCount',
    label: 'Ucz.',
    width: getSummaryColumnWidth('summary', 'effectiveParticipantsCount'),
    render: (row) => {
      if (
        typeof row.effectiveParticipantsCount === 'number' &&
        row.effectiveParticipantsCount > 0
      ) {
        return row.effectiveParticipantsCount;
      }

      const participantsLength = row.participants?.length ?? 0;
      if (participantsLength > 0) {
        return participantsLength;
      }

      return row.declaredParticipantsCount ?? 0;
    },
  },
  {
    key: 'totalPrice',
    label: 'Cena całk.',
    width: getSummaryColumnWidth('summary', 'totalPrice'),
    render: (row) => `${row.totalPrice.toFixed(2)} PLN`,
  },
  {
    key: 'paymentStatus',
    label: 'Status',
    width: getSummaryColumnWidth('summary', 'paymentStatus'),
    render: (row) => {
      const status = row.computedPaymentStatus || row.paymentStatus;
      const totalPaid =
        row.totalPaidAmount ?? ((row.paidAmount ?? 0) + (row.nonCashPaymentAmount ?? 0));
      const normalized = status || 'Nieopłacona';
      const amount = getPaymentStatusAmount(normalized, row.totalPrice, totalPaid);
      return (
        <Chip
          label={
            amount ? (
              <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
                <div>{normalizePaymentStatus(normalized)}</div>
                <div style={{ fontSize: '0.75em', opacity: 0.85 }}>{amount}</div>
              </Box>
            ) : (
              formatPaymentStatusLabel(normalized, row.totalPrice, totalPaid)
            )
          }
          color={getPaymentStatusColor(status)}
          size="small"
          sx={amount ? { height: 'auto', '& .MuiChip-label': { py: 0.5 } } : undefined}
        />
      );
    },
  },
  {
    key: 'totalPaidAmount',
    label: 'Wpłacono',
    width: getSummaryColumnWidth('summary', 'totalPaidAmount'),
    render: (row) => {
      const totalPaid =
        row.totalPaidAmount ?? ((row.paidAmount ?? 0) + (row.nonCashPaymentAmount ?? 0));
      return `${totalPaid.toFixed(2)} PLN`;
    },
  },
  {
    key: 'invoices',
    label: 'FV',
    width: getSummaryColumnWidth('summary', 'invoices'),
    render: (row) => <ReservationInvoiceLinksCell reservationId={row.id} />,
  },
  {
    key: 'status',
    label: 'Status rezerwacji',
    width: getSummaryColumnWidth('summary', 'status'),
    render: renderReservationStatus,
  },
];

export const createReservationColumns = ({
  applicantDisplayMode = 'split',
  parties = [],
}: ReservationColumnsOptions = {}): ColumnConfig<Reservation>[] => {
  if (applicantDisplayMode === 'summary') {
    return createReservationSummaryColumns(parties);
  }

  return [
  {
    key: 'createdAt',
    label: 'Data utworzenia',
    render: (row) => dayjs(row.createdAt).format('DD.MM.YYYY'),
  },

  {
    key: 'unitPrice',
    label: 'Cena jednostkowa',
    render: (row) => row.unitPrice + ' PLN',
  },
  {
    key: 'offerName',
    label: 'Nazwa oferty',
    render: (row) => renderOfferName(row, 'split'),
  },
  {
    key: 'applicantFirstName',
    label: 'Imię',
    render: (row: Reservation) => {
      const firstName = row.applicantFirstName?.trim();
      return firstName || '-';
    },
  },
  {
    key: 'applicantLastName',
    label: 'Nazwisko',
    render: (row: Reservation) => {
      const lastName = row.applicantLastName?.trim();
      return lastName || '-';
    },
  },
  {
    key: 'participantsCount',
    label: 'Liczba uczestników',
    render: (row) => {
      const countFromFlat = row.participantsCount;
      if (typeof countFromFlat === 'number') {
        return countFromFlat;
      }

      return row.participants?.length ?? 0;
    },
  },
  {
    key: 'effectiveParticipantsCount',
    label: 'Liczba uczestników (efektywna)',
    render: (row) => {
      if (
        typeof row.effectiveParticipantsCount === 'number' &&
        row.effectiveParticipantsCount > 0
      ) {
        return row.effectiveParticipantsCount;
      }

      const participantsLength = row.participants?.length ?? 0;
      if (participantsLength > 0) {
        return participantsLength;
      }

      return row.declaredParticipantsCount ?? 0;
    },
  },
  {
    key: 'applicantPartyId',
    label: 'Strona (zgłaszający)',
    render: (row) => (row.applicantPartyId ? `#${row.applicantPartyId}` : '-'),
  },
  {
    key: 'invoiceBuyerPartyId',
    label: 'Strona (nabywca FV)',
    render: (row) => {
      const value = row.invoiceBuyerPartyId ?? row.applicantPartyId;
      return value ? `#${value}` : '-';
    },
  },
  {
    key: 'fundingPartyId',
    label: 'Strona (finansujący)',
    render: (row) => (row.fundingPartyId ? `#${row.fundingPartyId}` : '-'),
  },
  {
    key: 'totalPrice',
    label: 'Cena całkowita',
    render: (row) => row.totalPrice.toFixed(2) + ' PLN',
  },
  {
    key: 'paymentStatus',
    label: 'Status płatności',
    render: (row) => {
      // Use computed status with fallback to stored status
      const status = row.computedPaymentStatus || row.paymentStatus;
      const totalPaid =
        row.totalPaidAmount ?? ((row.paidAmount ?? 0) + (row.nonCashPaymentAmount ?? 0));
      const normalized = status || 'Nieopłacona';
      const amount = getPaymentStatusAmount(normalized, row.totalPrice, totalPaid);
      return (
        <Chip
          label={
            amount ? (
              <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
                <div>{normalizePaymentStatus(normalized)}</div>
                <div style={{ fontSize: '0.75em', opacity: 0.85 }}>{amount}</div>
              </Box>
            ) : (
              formatPaymentStatusLabel(normalized, row.totalPrice, totalPaid)
            )
          }
          color={getPaymentStatusColor(status)}
          size="small"
          sx={amount ? { height: 'auto', '& .MuiChip-label': { py: 0.5 } } : undefined}
        />
      );
    },
  },
  {
    key: 'totalPaidAmount',
    label: 'Wpłacono',
    render: (row) => {
      // Use totalPaidAmount (cash + transfer) with fallback to paidAmount (legacy)
      const totalPaid =
        row.totalPaidAmount ?? ((row.paidAmount ?? 0) + (row.nonCashPaymentAmount ?? 0));
      return `${totalPaid.toFixed(2)} PLN`;
    },
  },
  {
    key: 'paymentMethod',
    label: 'Metoda płatności',
    render: (row) => {
      // Use computed method with fallback to stored method
      const method = row.computedPaymentMethod || row.paymentMethod;
      return method ? PaymentMethodTypeLabels[method] : '-';
    },
  },
  {
    key: 'source',
    label: 'Źródło',
    render: (row) => row.source,
  },
  {
    key: 'status',
    label: 'Status rezerwacji',
    render: renderReservationStatus,
  },
  ];
};

export const reservationColumns: ColumnConfig<Reservation>[] = createReservationColumns();
